import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useWorkspace } from '../state/workspace.tsx';
import { CALL_LINK_LIFETIME, callLaunchIntents } from './launchIntents.ts';
import { useRouter } from '../lib/router.tsx';
import { ContextCard } from '../features/phone/ContextCard.tsx';
import type { CallSessionLink } from './deepLink.ts';

export function callLinkReviewCopy(session?: CallSessionLink): string {
  return session
    ? 'Start or Resume in CRM authorized this session call. The phone starts it when ready. Use Pause or Stop in CRM to prevent the next call.'
    : 'Check the destination and choose the outgoing line in the phone. Press Call to start this call.';
}

export function CallLinkPage() {
  const ws = useWorkspace();
  const router = useRouter();
  const intents = callLaunchIntents();
  const snapshot = useSyncExternalStore(intents.subscribe, intents.getSnapshot, intents.getSnapshot);
  const [clock, setClock] = useState(0);
  const busy = ws.liveCalls.length > 0 || ws.bridge.calls.some(c => c.phase !== 'ended' && c.phase !== 'failed') || ws.callControl.starting.length > 0;
  const owner = !ws.phoneOwnership || ws.phoneOwnership.status === 'owner';
  const handoffRequested = useRef(false);
  useEffect(() => {
    if (router.query) {
      handoffRequested.current = false;
      intents.capture(`${window.location.origin}${router.path}${router.query}`, window.name);
    }
    router.navigate('/call', { replace: true });
  }, [router.query]);
  useEffect(() => { const timer = setInterval(() => setClock(c => c + 1), 1000); return () => clearInterval(timer); }, []);
  useEffect(() => {
    if (ws.config.loading || ws.numbers.loading) return;
    if (snapshot.pending && !busy && !owner && ws.phoneOwnership?.status === 'observer' && ws.phoneOwnership.owner?.kind === 'tab' && !ws.phoneOwnership.owner.busy && !handoffRequested.current) {
      handoffRequested.current = true;
      ws.phone.useHere();
    }
    if (snapshot.review && busy) {
      intents.cancel('Your phone is busy starting, ringing or on a call. No linked call was queued.');
      ws.cancelDial?.();
      return;
    }
    if (!snapshot.review && (ws.dialRequest?.origin === 'link' || ws.dialRequest?.origin === 'session')) ws.cancelDial?.();
    const intent = intents.deliver(busy, owner);
    if (intent) ws.requestDial({ kind: 'pstn', input: intent.e164, fromNumberId: null }, null, intent.session ? 'session' : 'link', intent.created + CALL_LINK_LIFETIME, intent.session);
  }, [snapshot.revision, busy, owner, ws.config.loading, ws.numbers.loading, ws.requestDial, ws.phoneOwnership, clock]);
  const cancel = () => { intents.cancel(); ws.cancelDial?.(); };
  const activeCall = ws.liveCalls[0] ?? null;
  return <div className="page page-narrow">
    <h1>Review call</h1>
    <p>{callLinkReviewCopy((snapshot.pending ?? snapshot.review)?.session ?? (ws.dialRequest?.origin === 'session' ? ws.dialRequest.session : undefined))}</p>
    {snapshot.pending || snapshot.review ? <p className="mono">{(snapshot.pending ?? snapshot.review)?.e164}</p> : null}
    {snapshot.message ? <p role="status" className="notice notice-info">{snapshot.message}</p> : null}
    {!owner ? <p>Your phone is in another window. Open that window, or move the idle phone to this installed app using the phone control.</p> : null}
    <button type="button" className="btn" onClick={cancel}>Cancel call request</button>
    {activeCall ? <ContextCard call={activeCall} /> : null}
  </div>;
}
